// externals
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// components
import { Alert } from '@interstate/components/Alert';
import { Typography } from '@interstate/components/Typography';
import { Button } from '@interstate/components/Button';
import { Modal } from '@interstate/components/Modal';

// style
import './confirmPushToDms.css';

// constants
import {
    CANCEL_BUTTON_TEXT,
    CLOSE_BUTTON_TEXT,
    DIALOG_HEADER_INTERSTATE,
    DIALOG_PROMPT,
    LOADING_TEXT_INTERSTATE,
    PUSH_ERROR_DIALOG_HEADER_INTERSTATE,
    PUSH_VIN_ERROR_DIALOG_MESSAGE,
    PUSH_STOCK_NUMBER_ERROR_DIALOG_MESSAGE
} from '../pushToDmsConstants';

// libraries
import { featureToggleSelectors, dealerSelectors } from '@makemydeal/dr-shared-store';
import { CDMS, DMS_DEALAPI_ONLY, DMS_PLUS, FIIA_DMS_INTEGRATIONS } from '@makemydeal/dr-dash-store/constants';
import * as deskingActionCreators from '@makemydeal/dr-dash-store/actions/deskingActionCreators';
import * as offerInfoSelectors from '@makemydeal/dr-dash-store/selectors/offerInfoSelectors';
import * as pushToDmsActionCreators from '@makemydeal/dr-dash-store/actions/pushToDmsActionCreators';
import * as pushToDmsSelectors from '@makemydeal/dr-dash-store/selectors/pushToDmsSelectors';
import * as tradeInSelectors from '@makemydeal/dr-dash-store/selectors/trade';
import * as tradeInsSelectors from '@makemydeal/dr-dash-store/selectors/tradeIns';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';

// types
import { PushToDmsStatus } from '../pushToDmsTypes';
import { Grid } from '@interstate/components/Grid';
import { usePushToDmsConfirmButtonText } from '../pushToDmsHooks';
import { useValidationGuard } from '../../../hooks/useValidationGuard';

export interface IConfirmPushToDmsDialogProps {
    show: boolean;
    onSaved(): void;
    onFailed(): void;
    onCancel(): void;
    onHide(): void;
    showLoader(): void;
    closeLoader(): void;
    setIsPushToCdmsActive(flag: boolean): void;
    setIsExtendedLoader(flag: boolean): void;
}

const ConfirmPushToDmsDialog = (props: IConfirmPushToDmsDialogProps) => {
    const MULTI_TRADE_IN_DEFAULT_INDEX = 0;
    const VALID_VIN_LENGTH = 17;

    const prefix = 'confirm-push-to-dms-dialog';
    const { show, onFailed, onSaved, onCancel, onHide, showLoader, closeLoader, setIsPushToCdmsActive, setIsExtendedLoader } =
        props;
    const [pushToDmsStatus, setPushToDmsStatus] = useState(PushToDmsStatus.Ready);

    const offerIsBeingSaved = useSelector(offerInfoSelectors.getIsOfferBeingSaved);
    const offerHasBeenSaved = useSelector(offerInfoSelectors.getHasBeenSaved);
    const cdmsRequestFailure = useSelector(pushToDmsSelectors.getCdmsRequestFailure);
    const hasCdmsResponseData = Object.keys(useSelector(pushToDmsSelectors.getCdmsResponseData)).length > 0;
    const returnedSuccessfulCdmsResponse = useSelector(pushToDmsSelectors.getReturnedSuccessfulCdmsResponse);
    const dmsIntegrationToggle = useSelector(dealerSelectors.getDMSIntegrationToggle);
    const isDmsIntegrationDealApiOnly = dmsIntegrationToggle === DMS_DEALAPI_ONLY;
    const isDmsIntegrationDmsPlus = dmsIntegrationToggle === DMS_PLUS;
    const isDmsIntegrationCdms = dmsIntegrationToggle === CDMS;

    const isShouldIncludeTradeIn = useSelector(tradeInsSelectors.getShouldIncludeTradeIn(MULTI_TRADE_IN_DEFAULT_INDEX));
    const vin = useSelector(tradeInsSelectors.getVin(MULTI_TRADE_IN_DEFAULT_INDEX));
    const shouldIncludeTradeIn = isShouldIncludeTradeIn && vin.trim().length > 0;
    const isTradeCompleted = useSelector(tradeInSelectors.isTradeInCompleted);
    const isTradeVinValid = vin && vin.trim().length === VALID_VIN_LENGTH;

    const vehicleStockNumber = useSelector(vehicleSelectors.getStockNumber);
    const isStockNumberValid = Boolean(vehicleStockNumber?.trim().length);

    const isEnableOptionalPbcUi = useSelector(featureToggleSelectors.enableOptionalPbcUi);

    const dispatch = useDispatch();

    const confirmBtnText = usePushToDmsConfirmButtonText();
    const guard = useValidationGuard();

    const handleShowChange = () => {
        if (show) {
            setPushToDmsStatus(PushToDmsStatus.Ready);
        }
    };

    const handleOfferStatusChange = () => {
        if (show) {
            if (offerIsBeingSaved === true) {
                return setPushToDmsStatus(PushToDmsStatus.Sending);
            }

            if (offerHasBeenSaved === false) {
                setTimeout(onFailed, 500);
                return;
            }

            if (offerHasBeenSaved === true && FIIA_DMS_INTEGRATIONS.includes(dmsIntegrationToggle)) {
                setTimeout(onSaved, 500);
                return;
            }
        }
    };

    const handlePushToDmsStatusChange = () => {
        if (show || (isEnableOptionalPbcUi && !returnedSuccessfulCdmsResponse)) {
            if (isEnableOptionalPbcUi) {
                closeLoader();
            }
            if (cdmsRequestFailure) {
                setTimeout(onFailed, 500);
                return;
            } else {
                if (hasCdmsResponseData) {
                    if (returnedSuccessfulCdmsResponse) {
                        setTimeout(onSaved, 500);
                        return;
                    }
                    setTimeout(onFailed, 500);
                    return;
                }
            }
        }
    };

    const handleConfirmClick = guard.wrap(() => {
        dispatch(deskingActionCreators.pushToDmsConfirmClicked());

        if (isDmsIntegrationDealApiOnly) dispatch(pushToDmsActionCreators.pushToDmsDealApiOnly());
        else if (isDmsIntegrationDmsPlus) dispatch(pushToDmsActionCreators.pushToDmsPlus());
        else {
            if (isEnableOptionalPbcUi) {
                onHide();
                showLoader();
                setIsPushToCdmsActive(true);
                setIsExtendedLoader(false);
            }
            dispatch(pushToDmsActionCreators.pushToDms());
        }
    });

    useEffect(handleShowChange, [show]);

    useEffect(handleOfferStatusChange, [offerIsBeingSaved, offerHasBeenSaved]);

    useEffect(handlePushToDmsStatusChange, [returnedSuccessfulCdmsResponse, hasCdmsResponseData, cdmsRequestFailure]);

    const header = (
        <Typography variant="h4" tag="h4">
            {DIALOG_HEADER_INTERSTATE}
        </Typography>
    );
    const hasStockError = isDmsIntegrationDealApiOnly && !isStockNumberValid;
    const hasVinError = shouldIncludeTradeIn && isTradeCompleted && !isTradeVinValid;
    const hasAnyError = hasVinError || hasStockError;
    const isVinOrStockValid = !hasVinError || isStockNumberValid;

    const footer = (
        <Grid container gap={1} justifyContent="flex-end" padding="16px 24px">
            {hasAnyError ? (
                <Button
                    data-testid={`${prefix}-cancel-button`}
                    buttonStyle={isVinOrStockValid ? 'tertiary' : 'secondary'}
                    onClick={onCancel}
                    disabled={pushToDmsStatus === PushToDmsStatus.Sending}
                >
                    {isVinOrStockValid ? CANCEL_BUTTON_TEXT : CLOSE_BUTTON_TEXT}
                </Button>
            ) : (
                <>
                    <Button
                        data-testid={`${prefix}-cancel-button`}
                        buttonStyle="tertiary"
                        onClick={onCancel}
                        disabled={pushToDmsStatus === PushToDmsStatus.Sending}
                    >
                        {CANCEL_BUTTON_TEXT}
                    </Button>
                    <Button
                        data-testid={`${prefix}-confirm-button`}
                        buttonStyle="primary"
                        onClick={handleConfirmClick}
                        disabled={pushToDmsStatus === PushToDmsStatus.Sending}
                    >
                        {pushToDmsStatus === PushToDmsStatus.Sending ? LOADING_TEXT_INTERSTATE : confirmBtnText}
                    </Button>
                </>
            )}
        </Grid>
    );

    const dialogDefaultBody = (
        <Typography variant="body-md" tag="p">
            {DIALOG_PROMPT}
        </Typography>
    );

    const dialogErrorBody = (
        <Grid paddingX={3}>
            <Alert data-testid="dms-error-modal" type="error">
                <Typography variant="h6" tag="h6">
                    {PUSH_ERROR_DIALOG_HEADER_INTERSTATE}
                </Typography>
                <ul style={{ margin: '0.57rem 0', paddingLeft: '1rem' }}>
                    {hasVinError && (
                        <li>
                            <Typography variant="body-sm" tag="span" data-testid="dms-error-detail-text">
                                {PUSH_VIN_ERROR_DIALOG_MESSAGE}
                            </Typography>
                        </li>
                    )}
                    {hasStockError && (
                        <li>
                            <Typography variant="body-sm" tag="span" data-testid="dms-error-stock-text">
                                {PUSH_STOCK_NUMBER_ERROR_DIALOG_MESSAGE}
                            </Typography>
                        </li>
                    )}
                </ul>
            </Alert>
        </Grid>
    );

    const getDialogBody = () => {
        if (hasVinError || hasStockError) return dialogErrorBody;
        return dialogDefaultBody;
    };

    return (
        <Modal
            size={isEnableOptionalPbcUi && isDmsIntegrationCdms ? 'large' : 'small'}
            data-testid={pushToDmsStatus === PushToDmsStatus.Sending ? `${prefix}-loading-container` : prefix}
            show={show}
            header={header}
            footer={{
                footerComponent: footer
            }}
            onHide={onHide}
        >
            {getDialogBody()}
        </Modal>
    );
};

export default ConfirmPushToDmsDialog;
